import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUserManagementLocations } from '../hooks/useUsers';
import { trpc } from '@/lib/trpc';
import ViewerPagePicker from './ViewerPagePicker';
import { DEFAULT_VIEWER_PAGES } from '@/lib/page-registry';
import { useEffect } from 'react';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { CLIENT_MODE_ROLE, CLIENT_ROLE_LABEL } from '../utils/role-style';
import { DTF_FLOOR_HINT, dtfFloorPick, pickerRoles } from '../utils/assignable-roles';
import type { UserRole } from '../types';

interface InviteUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Forces client mode when true; omitted follows the server's pure-client verdict.
   * The onboarding invite step passes true for a client org.
   */
  clientMode?: boolean;
}

export default function InviteUserModal({ open, onOpenChange, clientMode }: InviteUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('Warehouse Staff');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [viewerVisiblePages, setViewerVisiblePages] = useState<string[]>([]);
  const { profile: _profile } = useAuth();

  // Client mode: a pure-client 3PL org has one login type, so the role picker and
  // the location picker both go away. See utils/role-style.ts for why the role
  // written is still `Admin`, and note the Admin exemption in AuthContext's
  // sign-in gate — an Admin with zero profile_locations rows can still log in,
  // which is what makes hiding the location picker safe.
  const { isPureClient, hidesProduction } = useOrgRoleSurfaces();
  const isClientMode = clientMode ?? isPureClient;
  const { organization } = useOrganization();
  const roleOptions = pickerRoles({ runsOwnFloor: !hidesProduction });

  // Seed picker with the default page list the first time admin selects Viewer.
  useEffect(() => {
    if (role === 'Viewer' && viewerVisiblePages.length === 0) {
      setViewerVisiblePages([...DEFAULT_VIEWER_PAGES]);
    }
  }, [role, viewerVisiblePages.length]);

  // Use hooks for data and operations
  const { locations } = useUserManagementLocations();
  // A DTF Printer is offered, and submits, only floors the org owns; the submit gate
  // counts those, not every box ticked before the role was switched.
  const { floors, submittedIds: submittedLocations, isDtf } = dtfFloorPick(
    locations ?? [],
    role,
    organization?.id,
    selectedLocations,
  );
  const needsFloor = isDtf && submittedLocations.length === 0;
  const utils = trpc.useUtils();
  const inviteUser = trpc.users.inviteUser.useMutation({
    onSuccess: (data) => {
      const inviteWasSent = !!data?.inviteSent;
      toast({
        title: inviteWasSent ? 'Invitation sent!' : 'User added to organization',
        description: inviteWasSent
          ? `An invitation email has been sent to ${email}`
          : `${email} already has an account and was granted access to this organization.`,
      });
      utils.users.list.invalidate();
      resetForm();
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: 'Failed to send invitation',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('Warehouse Staff');
    setSelectedLocations([]);
    setViewerVisiblePages([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Pin the role at the mutation rather than relying on the hidden picker's
    // state: `role` defaults to Warehouse Staff, and a Warehouse Staff invite with
    // no locations produces a user who can never sign in.
    const effectiveRole = isClientMode ? CLIENT_MODE_ROLE : role;
    inviteUser.mutate({
      email,
      firstName,
      lastName,
      role: effectiveRole,
      locationIds:
        !isClientMode && submittedLocations.length > 0 ? submittedLocations : undefined,
      ...(effectiveRole === 'Viewer' ? { viewerVisiblePages } : {}),
    });
  };

  const toggleLocation = (locationId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px] max-h-[90dvh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an email invitation to a new user. They'll set their own password when they accept.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                An invitation link will be sent to this email
              </p>
            </div>
            {!isClientMode && (
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* "Super Admin" is a Blue Monk developer role, granted out of band
                      via service-role script — the API refuses to assign it. */}
                  {roleOptions.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            {!isClientMode && (
            <div className="space-y-2">
              <Label>{isDtf ? 'Location Access' : 'Location Access (Optional)'}</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                {floors.map((location) => (
                  <div key={location.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={location.id}
                      checked={selectedLocations.includes(location.id)}
                      onCheckedChange={() => toggleLocation(location.id)}
                    />
                    <Label
                      htmlFor={location.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {location.name}
                    </Label>
                  </div>
                ))}
              </div>
              {isDtf && (
                <p className="text-xs text-muted-foreground">
                  {DTF_FLOOR_HINT}
                </p>
              )}
              {needsFloor && (
                <p className="text-xs text-destructive">
                  Select at least one location. A user with no location access can’t sign in.
                </p>
              )}
            </div>
            )}

            {!isClientMode && role === 'Viewer' && (
              <ViewerPagePicker value={viewerVisiblePages} onChange={setViewerVisiblePages} />
            )}

            <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                {isClientMode
                  ? `They will join as ${CLIENT_ROLE_LABEL}, with full access to this organization, and will receive an email with a secure link to create their account and set their password.`
                  : 'The invited user will receive an email with a secure link to create their account and set their password.'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={inviteUser.isPending || needsFloor}>
              {inviteUser.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
